'use client'

import type { StaticImageData } from 'next/image'
import type { Swiper as SwiperType } from 'swiper'

import { useEffect, useRef, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
// oxlint-disable-next-line no-restricted-imports -- grandfathered; audit before touching (stuck branches can be load-bearing)
import { useMediaQuery } from 'react-responsive'
import parse from 'html-react-parser'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import '../sections.css'

import LeftIcon from '@/assets/images/left-icon.svg'
import ProductAffluent from '@/assets/images/product-affluent.webp'
import ProductCreditCards from '@/assets/images/product-credit-cards.webp'
import ProductDeposits from '@/assets/images/product-deposits.webp'
import ProductFinancing from '@/assets/images/product-financing.webp'
import ProductWealthManagement from '@/assets/images/product-wealth-management.webp'
import RightIcon from '@/assets/images/right-icon.svg'
import Link from '@/components/Link'

interface ProductItem {
  label: string
  link: string
}

interface ProductCategory {
  name: string
  image: StaticImageData
  items: ProductItem[]
}

const productCategories: ProductCategory[] = [
  {
    name: 'Deposits',
    image: ProductDeposits,
    items: [
      { label: 'Savings Account-<i>i</i>', link: '/personal/deposits/savings-account' },
      {
        label: 'DuitPlus Savings Account-<i>i</i>',
        link: '/personal/deposits/duitplus-savings-account-i'
      },
      { label: 'Current Account-<i>i</i>', link: '/personal/deposits/current-account' },
      {
        label: 'Commodity Murabahah Term Deposit-<i>i</i>',
        link: '/personal/deposits/term-deposit-i'
      },
      {
        label: 'Commodity Murabahah Saving Account-<i>i</i>',
        link: '/personal/deposits/commodity-murabahah-savings-account-i'
      },
      {
        label: 'Commodity Murabahah Current Account-<i>i</i>',
        link: '/personal/deposits/commodity-murabahah-current-account-i'
      },
      {
        label: 'Commodity Murabahah Foreign Currency Current Account-<i>i</i>',
        link: '/personal/deposits/commodity-murabahah-foreign-currency-current-account-i'
      }
    ]
  },
  {
    name: 'Financing',
    image: ProductFinancing,
    items: [
      { label: 'Personal Financing-<i>i</i>', link: '/personal/financing/personal-financing-i' },
      {
        label: 'Micro Personal Financing-<i>i</i>',
        link: '/personal/financing/micro-personal-financing-i'
      },
      {
        label: 'Motorcycle Personal Financing-<i>i</i>',
        link: '/personal/financing/personal-financing-i-for-motorcycle'
      },
      {
        label: 'Personal Financing-<i>i</i> Umrah',
        link: '/personal/financing/personal-financing-i-umrah'
      },
      {
        label: 'Personal Financing-<i>i</i> Secured by Term Deposit-<i>i</i>',
        link: '/personal/financing/personal-financing-against-term-deposit'
      },
      {
        label: 'Personal Financing-<i>i</i> Employee Banking Solution',
        link: '/personal/financing/personal-financing-employee-banking'
      },
      {
        label: 'Corporate Community Select Programme – Salary Deduction',
        link: '/personal/financing/personal-financing-i-ccsp-sd'
      },
      { label: 'Home Financing-<i>i</i>', link: '/personal/financing/home-financing-i' },
      { label: 'Home One Account', link: '/personal/financing/home-refinancing-one-account' },
      { label: 'Home Financing-<i>i</i> SJKP', link: '/personal/financing/home-financing-i-sjkp' },
      {
        label: 'Business Premises Financing-<i>i</i>',
        link: '/personal/financing/business-financing'
      }
    ]
  },
  {
    name: 'Credit cards',
    image: ProductCreditCards,
    items: [
      { label: 'Visa Platinum Credit Card-<i>i</i>', link: '/personal/cards/credit-cards' },
      { label: 'Visa Signature Credit Card-<i>i</i>', link: '/personal/cards/credit-cards' }
    ]
  },
  {
    name: 'Wealth management',
    image: ProductWealthManagement,
    items: [
      { label: 'GoldInvest-<i>i</i>', link: '/personal/wealth/gold-investment-account' },
      { label: 'Unit Trusts', link: '/personal/wealth/unit-trust' },
      { label: 'Structured Product-<i>i</i>', link: '/personal/wealth/structured-product' },
      { label: 'Sinar Kasih', link: '/personal/wealth/sinar-kasih' },
      { label: 'Sinar Link Aiman', link: '/personal/wealth/sinar-link-aiman' },
      { label: 'Sinar Wasilah', link: '/personal/wealth/sinar-wasilah' },
      { label: 'Will Writing', link: '/personal/wealth/will-writing' }
    ]
  },
  {
    name: 'Affluent',
    image: ProductAffluent,
    items: [
      { label: 'Affluent Banking', link: '/personal/affluent' },
      { label: 'Diamond Affluent Banking', link: '/personal/affluent' }
    ]
  }
]

function Products() {
  const [activeIndex, setActiveIndex] = useState(0)
  const tabsSwiperRef = useRef<SwiperType | null>(null)

  const isMobile = useMediaQuery({ maxWidth: 767 })
  const activeCategory = productCategories[activeIndex]

  useEffect(() => {
    if (isMobile && tabsSwiperRef.current) {
      tabsSwiperRef.current.slideTo(activeIndex, 300, false)
    }
  }, [activeIndex, isMobile])

  const isFirst = activeIndex === 0
  const isLast = activeIndex === productCategories.length - 1

  return (
    <section className="h-100 pt-md-5">
      <Container className="pt-5">
        {/* HEADER */}
        <Row className="justify-content-center">
          <Col md={8} className="text-center mb-4">
            <h2 className="alrajhi-black fw-bold fs-26">Shariah-compliant banking services</h2>
            <p className="gray-500 fs-18 fs-16 lh-normal">
              An international Islamic bank backed by Saudi expertise, dedicated to providing sound
              financial solutions for customers across Malaysia.
            </p>
          </Col>
        </Row>

        {/* CATEGORY TABS */}
        <div className="category-tabs-wrapper">
          <Swiper
            slidesPerView="auto"
            spaceBetween={14}
            watchOverflow
            className="category-tabs"
            onSwiper={swiper => (tabsSwiperRef.current = swiper)}
          >
            {productCategories.map((cat, index) => (
              <SwiperSlide key={cat.name} style={{ width: 'auto' }}>
                <button
                  className={`category-tab fs-14  ${index === activeIndex ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  {cat.name}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* MAIN CONTENT */}
        <Row className="gy-4 align-items-start justify-content-start mt-md-4 mt-3">
          <Col lg={5}>
            <img
              src={activeCategory.image.src}
              width={activeCategory.image.width}
              height={activeCategory.image.height}
              alt={activeCategory.name}
              className="img-fluid rounded-4 w-100"
            />
          </Col>

          <Col lg={7}>
            <div className="banking-services pt-3 ps-lg-4">
              <h3 className="fw-bold mb-4 fs-4 text-lg-start text-center">{activeCategory.name}</h3>

              <ul className="product-list arrow-marker">
                {activeCategory.items.map(item => (
                  <li key={item.label} className="mb-2">
                    <Link
                      className="product-link text-reset text-decoration-none main bg-transparent border-0 p-0 text-start d-inline-block"
                      href={item.link}
                    >
                      <p className="mb-0">{parse(item.label)}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </Col>
        </Row>

        {/* BOTTOM NAV */}
        <div className="d-flex justify-content-center gap-4 mt-md-5 mt-3">
          {/* LEFT */}
          <button
            className={`arrow-btn ${isFirst ? 'disabled' : 'active'}`}
            onClick={() => !isFirst && setActiveIndex(i => i - 1)}
            disabled={isFirst}
            aria-label="Previous category"
          >
            <img src={LeftIcon.src} alt="Previous" width={18} height={18} />
          </button>

          {/* RIGHT */}
          <button
            className={`arrow-btn ${isLast ? 'disabled' : 'active'}`}
            onClick={() => !isLast && setActiveIndex(i => i + 1)}
            disabled={isLast}
            aria-label="Next category"
          >
            <img src={RightIcon.src} alt="Next" width={18} height={18} />
          </button>
        </div>
      </Container>
    </section>
  )
}

export default Products
